'use client';

import { useEffect, useState } from 'react';
import { Alert, Box, styled } from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist';
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import LinkOptional from "@/app/features/shared/__to_be_integrated/ui/LinkOptional";
import Image from "next/image";

// Set the worker source
if (typeof window !== 'undefined') {
    const workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url);
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc.toString();
}

export interface PDFViewProps {
    src: string;
    onLoad?: () => void;
    onError?: (error: Error) => void;
    width?: string | number;
    height?: string | number;
    clickToOpen?: boolean;
}

type PdfPreviewCacheEntry = {
    objectUrl: string;
};

const PDF_PREVIEW_CACHE = new Map<string, PdfPreviewCacheEntry>();
const PDF_PREVIEW_CACHE_MAX_ENTRIES = 24;
const PDF_PREVIEW_SCALE = 1.1;

function setPreviewInCache(src: string, entry: PdfPreviewCacheEntry) {
    if (!PDF_PREVIEW_CACHE.has(src) && PDF_PREVIEW_CACHE.size >= PDF_PREVIEW_CACHE_MAX_ENTRIES) {
        const oldestKey = PDF_PREVIEW_CACHE.keys().next().value as string | undefined;
        if (oldestKey) {
            const staleEntry = PDF_PREVIEW_CACHE.get(oldestKey);
            if (staleEntry) {
                URL.revokeObjectURL(staleEntry.objectUrl);
            }
            PDF_PREVIEW_CACHE.delete(oldestKey);
        }
    }
    PDF_PREVIEW_CACHE.set(src, entry);
}

export default function PDFView({ src, onLoad, onError, width = '100%', height = 'auto', clickToOpen = true }: PDFViewProps) {
    const [preview, setPreview] = useState<PdfPreviewCacheEntry | null>(() => {
        if (!src) return null;
        return PDF_PREVIEW_CACHE.get(src) ?? null;
    });
    const [isLoading, setIsLoading] = useState(() => !!src && !PDF_PREVIEW_CACHE.has(src));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!src) {
            const errorMessage = 'No PDF source provided';
            setPreview(null);
            setError(errorMessage);
            setIsLoading(false);
            onError?.(new Error(errorMessage));
            return;
        }

        const cachedPreview = PDF_PREVIEW_CACHE.get(src);
        if (cachedPreview) {
            setPreview(cachedPreview);
            setError(null);
            setIsLoading(false);
            onLoad?.();
            return;
        }

        let cancelled = false;
        let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;
        let createdObjectUrl: string | null = null;

        (async () => {
            try {
                setPreview(null);
                setIsLoading(true);
                setError(null);

                const proxyUrl = `/api/proxy?url=${encodeURIComponent(src)}`;
                loadingTask = pdfjsLib.getDocument(proxyUrl);
                const pdf = await loadingTask.promise;
                const page = await pdf.getPage(1);
                const viewport = page.getViewport({ scale: PDF_PREVIEW_SCALE });

                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const context = canvas.getContext('2d', { alpha: false });
                if (!context) {
                    throw new Error('Could not get canvas context');
                }

                await page.render({
                    canvasContext: context,
                    viewport,
                }).promise;

                const blob = await new Promise<Blob>((resolve, reject) => {
                    canvas.toBlob((nextBlob) => {
                        if (!nextBlob) {
                            reject(new Error('Failed to create PDF preview blob'));
                            return;
                        }
                        resolve(nextBlob);
                    }, 'image/webp', 0.8);
                });

                createdObjectUrl = URL.createObjectURL(blob);

                if (cancelled) {
                    URL.revokeObjectURL(createdObjectUrl);
                    return;
                }

                const previewEntry = { objectUrl: createdObjectUrl };
                setPreview(previewEntry);
                setPreviewInCache(src, previewEntry);
                setIsLoading(false);
                onLoad?.();
            } catch (nextError) {
                if (cancelled) return;
                console.error('Error converting PDF to image:', nextError);
                const errorMessage = 'Failed to load PDF preview';
                setError(errorMessage);
                setIsLoading(false);
                onError?.(new Error(errorMessage));
            }
        })();

        return () => {
            cancelled = true;
            if (loadingTask) {
                void loadingTask.destroy();
            }
            if (createdObjectUrl && !PDF_PREVIEW_CACHE.has(src)) {
                URL.revokeObjectURL(createdObjectUrl);
            }
        };
    }, [onError, onLoad, src]);

    return (
        <Box
            className="pdf-preview-container"
            sx={{
                width,
                height,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '4px',
            }}
        >
            {isLoading && (
                <LoadingContainer>
                    <LoadingSpinner />
                </LoadingContainer>
            )}

            {error && !isLoading && (
                <ErrorContainer>
                    <Alert severity="error">{error}</Alert>
                </ErrorContainer>
            )}

            {preview && !isLoading && (
                <LinkOptional enabled={clickToOpen} href={src} target="_blank" rel="noopener noreferrer">
                    <ImageContainer>
                        {clickToOpen && (
                            <Overlay>
                                <Box sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    zIndex: 10,
                                }}>
                                    PDF öffnen
                                </Box>
                            </Overlay>
                        )}
                        <Image
                            src={preview.objectUrl}
                            alt="PDF preview"
                            fill
                            unoptimized
                            sizes="100vw"
                            style={{ objectFit: 'contain', zIndex: 5 }}
                        />
                    </ImageContainer>
                </LinkOptional>
            )}
        </Box>
    );
}

const LoadingContainer = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const ErrorContainer = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px',
});

const ImageContainer = styled('div')({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
        '& > div': {
            opacity: 1,
        }
    },
});

const Overlay = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
});
